import { ManagerAuthorization } from '@common/enums';
import { hasOneAuthorization } from '@frontend/utils';
import type { IOrganization, IPractitioner } from './AdminProcessDetails';
import type { AdminProcess } from './adminProcessDetailsTypes';
import { RequestStatus } from '../../../../common/enums/requestStatus';
import { TaskStatus, TaskType } from '../../../../common/enums/task';
import { getDisplayName } from '../../../../common/utils/getDisplayName';
import { translateKey } from '../../../translate';
import { Link } from '../../elements/common';
import { FlexColumns } from '../../elements/FlexColumns';
import { HorizontalFormGroupRead } from '../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../elements/forms/InputContainer';
import { ReadFormLabel } from '../../elements/forms/ReadFormLabel';
import { InlineReadElement } from '../../elements/inlineReadElement/InlineReadElement';

interface IPractitionerAndOrganizationProps {
    adminProcess: AdminProcess;
    alternativeText?: string;
    entity: 'request' | 'procedure' | 'diagnosticReport' | 'supervision';
    label: string;
    organization?: IOrganization | null;
    organizationId?: string | null;
    practitioner?: IPractitioner | null;
    practitionerId?: string | null;
}

export function PractitionerAndOrganizationActors(props: IPractitionerAndOrganizationProps) {
    let practitionerText;
    let practitionerLink = '';
    let organizationText;
    let organizationLink = '';

    if (props.alternativeText) {
        practitionerText = props.alternativeText;
    } else {
        if (props.practitioner && props.practitionerId) {
            practitionerLink = props.practitioner.uid ? props.practitioner.uid : props.practitionerId;
            practitionerText = getDisplayName(props.practitioner);
        }

        if (props.organization && props.organizationId) {
            organizationLink = props.organization.txcsId ? props.organization.txcsId : props.organizationId;
            organizationText = props.organization.name;
        }
    }

    let isRecipient = false;
    if (props.organizationId) {
        isRecipient =
            props.organizationId === props.adminProcess.recipientOrganizationId &&
            (!props.adminProcess.recipientPractitionerId ||
                props.adminProcess.recipientPractitionerId === props.practitionerId);
    }

    const supervisionTask = props.adminProcess.tasks?.find(
        (task) =>
            task.type === TaskType.supervise &&
            (task.status === TaskStatus.accepted || task.status === TaskStatus.open) &&
            task.performerOrganizationId === props.organizationId &&
            task.performerPractitionerId === props.practitionerId
    );
    if (props.entity === 'supervision' && supervisionTask) {
        isRecipient = true;
    }

    if (props.entity !== 'supervision' && props.adminProcess.status === RequestStatus.completed) {
        isRecipient = false;
    }

    return (
        <HorizontalFormGroupRead>
            <ReadFormLabel>{props.label}</ReadFormLabel>
            <InputContainer>
                <FlexColumns sm={12}>
                    <>
                        {showText(practitionerText, isRecipient && !!props.adminProcess.recipientPractitionerId)}
                        {showLink(
                            practitionerLink,
                            `/practitioners/${props.practitionerId}/profile`,
                            hasOneAuthorization(ManagerAuthorization.practitionerRead)
                        )}
                        {props.adminProcess.requesterAutoRights ||
                        props.adminProcess.procedurePerformerAutoRights ||
                        props.adminProcess.lastDiagnosticReportPerformerAutoRights
                            ? showAutoRights(props.entity)
                            : null}
                    </>
                    <>
                        {showText(organizationText, isRecipient && !props.adminProcess.recipientPractitionerId)}
                        {showLink(
                            organizationLink,
                            `/organizations/${props.organizationId}/view`,
                            hasOneAuthorization(ManagerAuthorization.organizationRead)
                        )}
                    </>
                </FlexColumns>
            </InputContainer>
        </HorizontalFormGroupRead>
    );

    function showText(text: string | undefined, isRequestRecipient: boolean) {
        const isBold = !!text;
        const color = text && isRequestRecipient ? 'primary' : !text ? 'muted' : undefined;

        return (
            <InlineReadElement
                bold={isBold}
                color={color}
            >
                {text ? text : translateKey('processDetails.unknown')}
            </InlineReadElement>
        );
    }

    function showLink(text: string | undefined, url: string, isLink: boolean) {
        return (
            <InlineReadElement>
                {isLink ? (
                    <Link
                        color="secondary"
                        to={url}
                    >
                        {text}
                    </Link>
                ) : (
                    text
                )}
            </InlineReadElement>
        );
    }

    function showAutoRights(entity: string) {
        const autoRightsText = translateKey('request.autoRights');
        switch (entity) {
            case 'procedure':
                return props.adminProcess.procedurePerformerAutoRights ? autoRightsText : null;
            case 'diagnosticReport':
                return props.adminProcess.lastDiagnosticReportPerformerAutoRights ? autoRightsText : null;
            case 'request':
                return props.adminProcess.requesterAutoRights ? autoRightsText : null;
            default:
                return null;
        }
    }
}
